import { toggleSelectPlaceholderState } from "./select-utils";

export function initAutocomplete(
  address1Field,
  address2Field,
  postalField,
  suburbField,
  cityField,
  provinceField,
) {
  if (
    !address1Field ||
    !address2Field ||
    !postalField ||
    !suburbField ||
    !cityField ||
    !provinceField
  )
    return;

  let autocomplete = new google.maps.places.Autocomplete(address1Field, {
    componentRestrictions: { country: ["za"] },
    fields: ["address_components", "geometry"],
    types: ["address"],
  });

  if (address1Field) {
    address1Field.focus();
  }

  autocomplete.addListener("place_changed", () =>
    fillInAddress(
      autocomplete,
      address1Field,
      address2Field,
      postalField,
      suburbField,
      cityField,
      provinceField,
    ),
  );
}

// Related parties does not have province on the model so autofill the rest
export function initRelatedPartyAutocomplete(
  address1Field,
  address2Field,
  postalField,
  suburbField,
  cityField,
) {
  if (
    !address1Field ||
    !address2Field ||
    !postalField ||
    !suburbField ||
    !cityField
  )
    return;

  let autocomplete = new google.maps.places.Autocomplete(address1Field, {
    componentRestrictions: { country: ["za"] },
    fields: ["address_components", "geometry"],
    types: ["address"],
  });

  if (address1Field) {
    address1Field.focus();
  }

  autocomplete.addListener("place_changed", () =>
    fillInAddress(
      autocomplete,
      address1Field,
      address2Field,
      postalField,
      suburbField,
      cityField,
      null,
    ),
  );
}

function generateGoogleAnalyticsId() {
  let pageTitle = "";

  if (document && document.title) {
    pageTitle = document.title.replace("SnapScan Merchant Portal", "");
  }

  if (pageTitle === "") {
    pageTitle = "Home";
  }

  return `PAC - ${pageTitle}`;
}

function fillInAddress(
  autocomplete,
  address1Field,
  address2Field,
  postalField,
  suburbField,
  cityField,
  provinceField,
) {
  // Creates an event for Google Tag Manager to track Places autofill clicks
  window.dataLayer.push({
    event: "pacClick",
    ga_identifier: generateGoogleAnalyticsId(),
  });

  const place = autocomplete.getPlace();
  if (!place || !place.address_components) return;

  let streetNumber = "";
  let route = "";
  let postcode = "";
  let suburb = "";

  for (const component of place.address_components) {
    const types = component.types || [];

    if (types.includes("street_number")) {
      streetNumber = component.long_name;
    }

    if (types.includes("route")) {
      route = component.long_name;
    }

    // Check all common types used for suburbs in South Africa
    if (
      types.includes("sublocality_level_1") ||
      types.includes("sublocality_level_2") ||
      types.includes("sublocality") ||
      types.includes("neighborhood")
    ) {
      // If we haven't found a suburb yet, or if this is a more specific sublocality_level_1
      if (!suburb || types.includes("sublocality_level_1")) {
        suburb = component.long_name;
      }
    }

    if (types.includes("postal_code")) {
      postcode = `${component.long_name}${postcode}`;
    }

    if (types.includes("postal_code_suffix")) {
      postcode = `${postcode}-${component.long_name}`;
    }

    if (types.includes("locality")) {
      cityField.value = component.long_name;
    }

    if (types.includes("administrative_area_level_1")) {
      if (provinceField) {
        provinceField.value = component.short_name;
        toggleSelectPlaceholderState();
      }
    }
  }

  address1Field.value = streetNumber ? `${streetNumber} ${route}` : route;
  postalField.value = postcode;
  if (suburbField) suburbField.value = suburb;

  address2Field.focus();
}

// Flexible address autocomplete that accepts a field mapping config.
export function initAddressAutocomplete(config) {
  if (!config || !config.addressField) return;

  const autocomplete = new google.maps.places.Autocomplete(config.addressField, {
    componentRestrictions: { country: ["za"] },
    fields: ["address_components", "formatted_address", "geometry"],
    types: ["address"],
  });

  autocomplete.addListener("place_changed", () => {
    fillInAddressFromConfig(autocomplete, config);
  });
}

function fillInAddressFromConfig(autocomplete, config) {
  const place = autocomplete.getPlace();
  if (!place || !place.address_components) return;

  const formattedAddress = place.formatted_address || "";
  let streetAddress = "";
  let postcode = "";

  for (const component of place.address_components) {
    if (!component.types) continue;

    const componentType = component.types[0];

    switch (componentType) {
      case "street_number":
        streetAddress = `${component.long_name} ${streetAddress}`;
        break;
      case "route":
        streetAddress += component.long_name;
        break;
      case "sublocality_level_1":
      case "sublocality_level_2":
        if (config.suburbField) config.suburbField.value = component.long_name;
        break;
      case "postal_code":
        postcode = `${component.long_name}${postcode}`;
        break;
      case "postal_code_suffix":
        postcode = `${postcode}-${component.long_name}`;
        break;
      case "locality":
        if (config.cityField) config.cityField.value = component.long_name;
        break;
      case "country":
        if (config.countryField) config.countryField.value = component.long_name;
        break;
      case "administrative_area_level_1":
        if (config.provinceField) {
          config.provinceField.value = component.short_name;
          toggleSelectPlaceholderState();
        }
        break;
    }
  }

  config.addressField.value = formattedAddress || streetAddress;
  if (config.postalField) config.postalField.value = postcode;
  if (config.address2Field) config.address2Field.focus();

  if (place.geometry && place.geometry.location) {
    if (config.latitudeField) {
      config.latitudeField.value = place.geometry.location.lat();
    }
    if (config.longitudeField) {
      config.longitudeField.value = place.geometry.location.lng();
    }
  }
}
